import React, { useState } from "react";
import { SavedContent } from "../../types/api/SavedContent";
import { SavedContentCard } from "./SavedContentCard";
import { PositionStates, PositionHandlers } from "./usePositionManagement";
import { UseFormSetValue, UseFormWatch, FieldValues } from "react-hook-form";
import { ConfirmationModal } from "../_ui/ConfirmationModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

interface SelectedSavedDescriptionsProps {
  selectedSavedDescriptions: number[];
  savedDescriptions: SavedContent[];
  positions: PositionStates;
  handlers: PositionHandlers;
  hashtagsField: string;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  onRemove: (id: number) => void;
  onReorder: (newOrder: number[]) => void;
  onRemoveHashtags?: (hashtags: string[]) => void;
  currentVariant?: string;
}

export function SelectedSavedDescriptions({
  selectedSavedDescriptions,
  savedDescriptions,
  positions,
  handlers,
  hashtagsField,
  setValue,
  watch,
  onRemove,
  onReorder,
  onRemoveHashtags,
  currentVariant = "main",
}: SelectedSavedDescriptionsProps) {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<{
    id: number;
    description: SavedContent;
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = selectedSavedDescriptions.indexOf(active.id as number);
      const newIndex = selectedSavedDescriptions.indexOf(over?.id as number);

      const newOrder = arrayMove(selectedSavedDescriptions, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  const handleRemoveClick = (id: number) => {
    const description = savedDescriptions.find((desc) => desc.id === id);
    if (!description) return;

    const hashtags =
      description.hashtags.variants[currentVariant] ??
      description.hashtags.main;

    if (hashtags && hashtags.length > 0) {
      setPendingRemoval({ id, description });
      setIsConfirmModalOpen(true);
    } else {
      // No hashtags, remove directly
      onRemove(id);
    }
  };

  const handleConfirmRemoval = () => {
    if (!pendingRemoval) return;

    const { id, description } = pendingRemoval;
    const hashtags =
      description.hashtags.variants[currentVariant] ??
      description.hashtags.main;

    // Remove the saved description
    onRemove(id);

    // Remove associated hashtags if callback is provided
    if (onRemoveHashtags && hashtags && hashtags.length > 0) {
      onRemoveHashtags(hashtags);
    }

    // Clean up state
    setIsConfirmModalOpen(false);
    setPendingRemoval(null);
  };

  const handleCancelRemoval = () => {
    if (!pendingRemoval) return;

    // Remove the saved description but keep hashtags
    onRemove(pendingRemoval.id);

    // Clean up state
    setIsConfirmModalOpen(false);
    setPendingRemoval(null);
  };

  if (selectedSavedDescriptions.length === 0) {
    return null;
  }

  const selected = selectedSavedDescriptions
    .map((id) => savedDescriptions.find((desc) => desc.id === id))
    .filter((desc): desc is SavedContent => desc !== undefined);

  return (
    <div>
      <div className="collapse collapse-plus rounded-none">
        <input type="checkbox" />
        <div className="collapse-title px-0 pt-0 pb-2">
          <div className="prose">
            <h5>
              Selected Saved Descriptions ({selectedSavedDescriptions.length})
            </h5>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {selected.map((desc) => {
              return (
                <div
                  className="badge badge-xs badge-soft badge-accent"
                  key={desc.id}
                >
                  {desc.name}
                </div>
              );
            })}
          </div>
        </div>
        <div className="collapse-content px-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedSavedDescriptions}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-2 gap-2 mb-2">
                {selected.map((desc) => (
                  <SavedContentCard
                    key={desc.id}
                    description={desc}
                    positions={{
                      content: positions.content[desc.id] || "USE_TEMPLATE",
                      hashtag: positions.hashtag[desc.id] || "START",
                      tag: positions.tag[desc.id] || "START",
                    }}
                    onPositionChange={{
                      content: (value) => handlers.content(desc.id, value),
                      hashtag: (value) => handlers.hashtag(desc.id, value),
                      tag: (value) => handlers.tag(desc.id, value),
                    }}
                    hashtagsField={hashtagsField}
                    setValue={setValue}
                    watch={watch}
                    onRemove={() => handleRemoveClick(desc.id)}
                    isDraggable={true}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
        title="Remove Saved Description"
        message={`Do you want to remove the hashtags associated with "${pendingRemoval?.description.name}" as well?`}
        confirmText="Remove Hashtags"
        cancelText="Keep Hashtags"
      />
    </div>
  );
}
